/**
 * Monetization Context - Smart Upgrade Prompt Engine
 * 
 * Provides centralized state management for:
 * - Quota-based upgrade prompts
 * - Feature-gated access control
 * - Contextual CRM upgrade triggers
 * - Analytics tracking
 * 
 * Usage:
 *   const { checkQuota, checkFeature, trackEvent, dismissPrompt } = useMonetization();
 *   
 *   // Before action
 *   const result = await checkQuota('leads', currentCount, maxLimit, 'lead_create');
 *   if (result.isBlocked) {
 *     // Show upgrade modal
 *   }
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useClinicData } from './ClinicDataContext';
import { useClinicScope } from '../hooks/useClinicScope';
import api from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Threshold constants
export const QUOTA_THRESHOLDS = {
  SOFT_WARNING: 70,
  UPGRADE_RECOMMENDATION: 85,
  HARD_LIMIT: 100,
};

// Prompt types
export const PROMPT_TYPES = {
  SOFT_WARNING: 'soft_warning',
  UPGRADE_RECOMMENDATION: 'upgrade_recommendation',
  HARD_LIMIT: 'hard_limit',
  FEATURE_GATE: 'feature_gate',
  CONTEXTUAL: 'contextual',
};

// Event types for tracking
export const PROMPT_EVENTS = {
  PROMPT_SHOWN: 'prompt_shown',
  MODAL_OPENED: 'modal_opened',
  UPGRADE_CLICKED: 'upgrade_clicked',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  PROMPT_DISMISSED: 'prompt_dismissed',
};

// Quota types
export const QUOTA_TYPES = {
  LEADS: 'leads',
  OFFERS: 'offers',
  SALES_REPS: 'sales_reps',
  DOCTORS: 'doctors',
  CLINICS: 'clinics',
  STORAGE: 'storage',
};

const MonetizationContext = createContext(null);

export const MonetizationProvider = ({ children }) => {
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const resolvedClinicId = rbacClinicId || clinicUser?.clinicId;
  const [activePrompt, setActivePrompt] = useState(null);
  const [dismissedPrompts, setDismissedPrompts] = useState({});
  
  /**
   * Check quota and return prompt if threshold reached
   */
  const checkQuota = useCallback(async (quotaType, current, maxLimit, context = 'general') => {
    if (!resolvedClinicId) {
      return { hasPrompt: false, prompt: null, isBlocked: false };
    }
    
    // Unlimited quota - no prompt needed
    if (maxLimit === -1) {
      return { hasPrompt: false, prompt: null, isBlocked: false };
    }
    
    // Calculate percentage locally for immediate response
    const percentage = maxLimit > 0 ? Math.round((current / maxLimit) * 100) : 100;
    
    // Check if at hard limit
    const isBlocked = percentage >= QUOTA_THRESHOLDS.HARD_LIMIT;
    
    // Check if below soft warning threshold
    if (percentage < QUOTA_THRESHOLDS.SOFT_WARNING) {
      return { hasPrompt: false, prompt: null, isBlocked: false, percentage };
    }
    
    // Check server for prompt config and tracking
    try {
      const response = await api.post(`/monetization/check-quota/${resolvedClinicId}`, {
          quota_type: quotaType,
          current,
          max_limit: maxLimit,
          context,
        });
      
      const data = response.data;
      return {
        hasPrompt: data.has_prompt,
        prompt: data.prompt,
        isBlocked: data.prompt?.is_blocking || false,
        percentage,
      };
    } catch (error) {
      console.error('[Monetization] Quota check error:', error);
    }
    
    // Fallback to local prompt
    return {
      hasPrompt: percentage >= QUOTA_THRESHOLDS.SOFT_WARNING,
      prompt: {
        type: isBlocked ? PROMPT_TYPES.HARD_LIMIT : 
              percentage >= QUOTA_THRESHOLDS.UPGRADE_RECOMMENDATION ? PROMPT_TYPES.UPGRADE_RECOMMENDATION :
              PROMPT_TYPES.SOFT_WARNING,
        quota_type: quotaType,
        current,
        max: maxLimit,
        percentage,
        is_blocking: isBlocked,
      },
      isBlocked,
      percentage,
    };
  }, [clinicUser]);
  
  /**
   * Check feature access for current plan
   */
  const checkFeature = useCallback(async (featureKey, currentPlan = 'TRIAL') => {
    if (!resolvedClinicId) {
      return { hasAccess: false, prompt: null };
    }
    
    try {
      const response = await api.post(`/monetization/check-feature/${resolvedClinicId}`, {
          feature_key: featureKey,
          current_plan: currentPlan,
        });
      
      const data = response.data;
      return {
        hasAccess: data.has_access,
        prompt: data.prompt,
      };
    } catch (error) {
      console.error('[Monetization] Feature check error:', error);
    }
    
    return { hasAccess: false, prompt: null };
  }, [clinicUser]);
  
  /**
   * Get contextual prompt for CRM action
   */
  const getContextualPrompt = useCallback(async (action, currentUsage, limits, currentPlan = 'TRIAL') => {
    if (!resolvedClinicId) {
      return null;
    }
    
    try {
      const response = await api.post(`/monetization/contextual-prompt/${resolvedClinicId}`, {
          action,
          current_usage: currentUsage,
          limits,
          current_plan: currentPlan,
        });
      
      const data = response.data;
      return data.prompt;
    } catch (error) {
      console.error('[Monetization] Contextual prompt error:', error);
    }
    
    return null;
  }, [clinicUser]);
  
  /**
   * Get dashboard upgrade recommendations
   */
  const getDashboardRecommendations = useCallback(async (currentPlan = 'TRIAL') => {
    if (!resolvedClinicId) {
      return [];
    }
    
    try {
      const response = await api.get(
        `/monetization/dashboard-recommendations/${resolvedClinicId}?current_plan=${currentPlan}`
      );
      
      const data = response.data;
      return data.recommendations || [];
    } catch (error) {
      console.error('[Monetization] Recommendations error:', error);
    }
    
    return [];
  }, [clinicUser]);
  
  /**
   * Track monetization event for analytics
   */
  const trackEvent = useCallback(async (eventType, metadata = {}) => {
    if (!resolvedClinicId) return;
    
    try {
      await api.post(`/monetization/track-event/${resolvedClinicId}`, {
          event_type: eventType,
          metadata,
        });
    } catch (error) {
      console.error('[Monetization] Track event error:', error);
    }
  }, [clinicUser]);
  
  /**
   * Dismiss prompt for a period (anti-spam)
   */
  const dismissPrompt = useCallback(async (promptType, quotaType = null, feature = null, durationHours = 24) => {
    if (!resolvedClinicId) return false;
    
    // Cannot dismiss hard limits
    if (promptType === PROMPT_TYPES.HARD_LIMIT) {
      return false;
    }
    
    // Track dismissal locally
    const key = `${promptType}:${quotaType || feature || 'general'}`;
    setDismissedPrompts(prev => ({
      ...prev,
      [key]: Date.now() + (durationHours * 3600 * 1000),
    }));
    
    // Clear active prompt
    setActivePrompt(null);
    
    // Track on server
    try {
      await api.post(`/monetization/dismiss-prompt/${resolvedClinicId}`, {
          prompt_type: promptType,
          quota_type: quotaType,
          feature,
          duration_hours: durationHours,
        });
      
      // Track dismissal event
      await trackEvent(PROMPT_EVENTS.PROMPT_DISMISSED, {
        prompt_type: promptType,
        quota_type: quotaType,
        feature,
      });
      
      return true;
    } catch (error) {
      console.error('[Monetization] Dismiss error:', error);
      return false;
    }
  }, [clinicUser, trackEvent]);
  
  /**
   * Check if prompt is locally dismissed
   */
  const isPromptDismissed = useCallback((promptType, quotaType = null, feature = null) => {
    const key = `${promptType}:${quotaType || feature || 'general'}`;
    const expiresAt = dismissedPrompts[key];
    return expiresAt && Date.now() < expiresAt;
  }, [dismissedPrompts]);
  
  /**
   * Show an upgrade prompt (sets active prompt)
   */
  const showPrompt = useCallback((prompt) => {
    setActivePrompt(prompt);
    trackEvent(PROMPT_EVENTS.PROMPT_SHOWN, {
      prompt_type: prompt?.type,
      quota_type: prompt?.quota_type,
      feature: prompt?.feature,
    });
  }, [trackEvent]);
  
  /**
   * Hide the active prompt
   */
  const hidePrompt = useCallback(() => {
    setActivePrompt(null);
  }, []);
  
  const value = {
    // State
    activePrompt,
    
    // Methods
    checkQuota,
    checkFeature,
    getContextualPrompt,
    getDashboardRecommendations,
    trackEvent,
    dismissPrompt,
    isPromptDismissed,
    showPrompt,
    hidePrompt,
    
    // Constants
    QUOTA_THRESHOLDS,
    PROMPT_TYPES,
    PROMPT_EVENTS,
    QUOTA_TYPES,
  };
  
  return (
    <MonetizationContext.Provider value={value}>
      {children}
    </MonetizationContext.Provider>
  );
};

export const useMonetization = () => {
  const context = useContext(MonetizationContext);
  if (!context) {
    throw new Error('useMonetization must be used within MonetizationProvider');
  }
  return context;
};

export default MonetizationContext;
